"use client";

import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type RevealOnScrollProps = {
  children: React.ReactNode;
  className?: string;
  once?: boolean;
  threshold?: number;
  y?: number;
  duration?: number;
  enableAnimation?: boolean;
  scaleStart?: number;
  x?: number;
  delay?: number; // in milliseconds
};

/**
 * RevealOnScroll is a component that reveals the children when they are visible in the viewport.
 * @param children - The children to reveal.
 * @param className - The class name to apply to the container.
 * @param once - If true, the animation will only trigger once.
 * @param threshold - The threshold to trigger the animation.
 * @param y - The distance to move the children down from the top.
 * @param duration - The duration of the animation.
 * @param scaleStart - The scale to start the animation at.
 * @param x - The distance to move the children left or right from the center.
 * @param enableAnimation - If false, the animation will be disabled and children will be visible immediately.
 * @param delay - The delay before the animation starts, in milliseconds.
 * @returns A React component that reveals its children on scroll.
 */
function RevealOnScroll({
  children,
  className = "",
  once = false, // if true, the animation will only trigger once
  threshold = 0.2, // if 20% visible trigger the animation
  y = 100, // 100px down from the top
  x=0, // for left and right animation
  duration = 800, // 800ms duration of the animation
  enableAnimation = true,
  scaleStart = 1,
  delay = 0, // delay before the animation starts
}: RevealOnScrollProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isVisibleRef = useRef(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    // No observer needed when animation is disabled. `enableAnimation` can toggle
    // at runtime (e.g. suppressing reveals after a user interaction), so this must
    // stay a hook that runs every render — never gate it behind an early return.
    if (!enableAnimation) return;
    const node = ref.current;
    if (!node) return;
    const enterThreshold = Math.min(Math.max(threshold, 0), 1);
    const exitThreshold = Math.min(0.05, enterThreshold * 0.5);
    const observerThresholds = Array.from(
      new Set([0, exitThreshold, enterThreshold]),
    ).sort((a, b) => a - b);

    const observer = new IntersectionObserver(
      ([entry]) => {
        const ratio = entry.intersectionRatio;

        if (ratio >= enterThreshold) {
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
          }
          if (!isVisibleRef.current) {
            isVisibleRef.current = true;
            setIsVisible(true);
            if (once) observer.unobserve(node);
          }
          return;
        }

        if (!once && ratio <= exitThreshold && isVisibleRef.current) {
          if (hideTimeoutRef.current) return;
          hideTimeoutRef.current = setTimeout(() => {
            if (!once) {
              isVisibleRef.current = false;
              setIsVisible(false);
            }
            hideTimeoutRef.current = null;
          }, 140);
          return;
        }

        if (hideTimeoutRef.current && ratio > exitThreshold) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      },
      {
        threshold: observerThresholds,
        // Shrink viewport bottom slightly so reveal starts closer to on-screen entry.
        // This prevents the animation from finishing before users see it during slow scroll.
        rootMargin: "0px 0px -4% 0px",
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, [once, threshold, enableAnimation]);

  // Rendered after all hooks so the hook order is identical whether or not the
  // animation is enabled — toggling `enableAnimation` at runtime is safe.
  if (!enableAnimation) {
    return children;
  }

  return (
    <div ref={ref} className={cn("p-0 m-0", className)}>
      <div
        className="transition-all ease-out will-change-transform p-0 m-0 w-full h-full"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible
            ? "translate(0px, 0px) scale(1)"
            : `translate(${x}px, ${y}px) scale(${scaleStart})`,
          transitionDuration: `${duration}ms`,
          transitionDelay: `${delay}ms`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

RevealOnScroll.displayName = "RevealOnScroll";
export default RevealOnScroll;
